import {
  inject,
  Injectable,
  signal,
  PLATFORM_ID,
  DOCUMENT,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PaySimpleJs } from '../../models/pay-simple.model';
import { InjectionToken } from '@angular/core';

export const WINDOW = new InjectionToken<Window>('Window object', {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  factory: () => inject(DOCUMENT).defaultView!,
});

@Injectable({
  providedIn: 'root',
})
export class WindowService {
  private platformId = inject(PLATFORM_ID);
  private window: Window | null;
  paySimpleJs = signal<PaySimpleJs | undefined>(undefined);
  grecaptcha: typeof grecaptcha | null | undefined;

  readonly viewportWidth = signal<number | null>(null);

  constructor() {
    this.window = isPlatformBrowser(this.platformId) ? inject(WINDOW) : null;
  }

  matchMedia(query: string): MediaQueryList {
    if (this.window && typeof this.window.matchMedia === 'function') {
      return this.window.matchMedia(query);
    }

    // Fallback for non-browser environments (SSR / tests).
    // Construct a minimal MediaQueryList-like object so callers can safely
    // call addEventListener/removeEventListener and read `matches`.
    const fallback: Partial<MediaQueryList> = {
      matches: false,
      media: query,
      onchange: null,
      addListener: () => {
        /* no-op for legacy API */
      },
      removeListener: () => {
        /* no-op for legacy API */
      },
      addEventListener: (
        _type: string,
        _listener: EventListenerOrEventListenerObject,
      ) => {
        /* no-op */
      },
      removeEventListener: (
        _type: string,
        _listener: EventListenerOrEventListenerObject,
      ) => {
        /* no-op */
      },
      dispatchEvent: (_ev: Event) => false,
    };

    return fallback as MediaQueryList;
  }

  getHostname(): string | null {
    return this.window ? this.window.location.hostname : null;
  }

  initPaysimpleJs(config: unknown): PaySimpleJs | null {
    return this.window ? this.window.paysimpleJs(config) : null;
  }
}

declare global {
  interface Window {
    google: typeof google | null | undefined;
    grecaptcha: typeof grecaptcha | null | undefined;
    paysimpleJs: (config: unknown) => PaySimpleJs;
  }
}
